import React, { useEffect, useState } from 'react';
import { Loader2, Maximize2, ImageOff } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import Tooltip from '../ui/Tooltip';
import ImageLightbox from '../ui/ImageLightbox';
import { useStorageUrlState } from '../chatHome/MemberAvatar';

/**
 * THE MODEL SHEET — the thing a character's consistency actually rests on, finally visible.
 *
 * Owner 2026-09-13: *"for cast, the character design is not showing the multi angle profile shots that needed
 * for character consistency"*. The angles were being GENERATED and USED — `generateCharacterTurnaround` writes
 * them and the render passes every one as a reference — but the project page showed a single front portrait,
 * so a creator could not see the sheet, could not tell whether the extra angles existed, and could not judge
 * the only question that matters about them: is this the same person from every side?
 *
 * That question can only be answered by comparing the angles at size, which is why the sheet opens into the
 * shared lightbox rather than trying to be legible as a row of thumbnails.
 *
 * THE TURNAROUND IS GENERATED IN THE BACKGROUND and best-effort: a character is usable from its front
 * reference alone, so a failed turnaround never blocked the cast, and it also never said anything. Here it
 * finally does, because "the angles are still coming" and "the angles never arrived" are different situations
 * and only one of them is worth waiting for.
 */
export interface CharacterSheetProps {
  name: string;
  /** The locked front reference. Absent means this character has no face yet. */
  facePath?: string | null;
  /** The extra angles: three-quarter, profile. Empty while the turnaround is still being made, or if it failed. */
  turnaroundPaths?: string[];
  /** True while the sheet is being fetched, so an empty row does not read as "no face". */
  loading?: boolean;
}

const ANGLE_LABEL = ['Three-quarter', 'Profile', 'Back', 'Upper body'];

const Thumb: React.FC<{ path: string; label: string; onOpen: () => void }> = ({ path, label, onOpen }) => {
  const { url, failed } = useStorageUrlState(path);
  return (
    <Tooltip content={`${label} — click to see it at size`}>
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Enlarge ${label}`}
        data-testid="sheet-angle"
        className="group relative block shrink-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-400"
      >
        {url ? (
          <>
            {/*
              BIG ENOUGH TO ANSWER THE QUESTION IT EXISTS FOR (owner 2026-09-20: *"the thumbnail image is
              small"*). These were 56px. The sheet's whole job is letting a creator judge whether five angles
              are the SAME PERSON, and a face at 56px cannot be compared to anything — so the control looked
              decorative and the enlarged view went undiscovered. Portrait aspect, because every view in the
              sheet is generated portrait.
            */}
            <img
              src={url}
              alt={label}
              className="h-32 w-24 rounded-md border border-secondary-200 object-cover transition-opacity group-hover:opacity-80 dark:border-secondary-700"
            />
            {/* The enlarge affordance says so on the picture itself, the same way a reference plate does. */}
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
              <Maximize2 className="h-4 w-4 text-white drop-shadow" aria-hidden />
            </span>
          </>
        ) : (
          <span className="flex h-32 w-24 flex-col items-center justify-center gap-1 rounded-md border border-secondary-200 px-1 text-center dark:border-secondary-700">
            {failed ? (
              /* Said in words, never a spinner that never stops (BUGS.md 2026-09-27). */
              <>
                <ImageOff className="h-3.5 w-3.5 text-secondary-400" aria-hidden />
                <span className={`${TYPE.micro} text-secondary-400`}>Could not load</span>
              </>
            ) : (
              <Loader2 className="h-3 w-3 animate-spin text-secondary-400" aria-hidden />
            )}
          </span>
        )}
        <span className={`mt-1 block truncate text-center ${TYPE.micro} text-secondary-400`}>{label.split(', ').pop()}</span>
      </button>
    </Tooltip>
  );
};

const CharacterSheet: React.FC<CharacterSheetProps> = ({ name, facePath, turnaroundPaths, loading }) => {
  const [openAt, setOpenAt] = useState<number | null>(null);
  const angles = (turnaroundPaths || []).filter(Boolean);

  useEffect(() => { if (!facePath) setOpenAt(null); }, [facePath]);
  if (!facePath && !loading) return null;

  const images = [
    ...(facePath ? [{ path: facePath, caption: `${name || 'This character'}, front` }] : []),
    ...angles.map((path, i) => ({ path, caption: `${name || 'This character'}, ${(ANGLE_LABEL[i] || `angle ${i + 2}`).toLowerCase()}` })),
  ];

  return (
    <div className="mt-2" data-testid="character-sheet">
      <div className="flex flex-wrap items-center gap-1.5">
        {loading && !facePath ? (
          <span className={`flex items-center gap-1.5 ${TYPE.micro} text-secondary-400`}>
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Fetching their sheet…
          </span>
        ) : (
          <>
            {facePath && <Thumb path={facePath} label={`${name || 'This character'}, front`} onOpen={() => setOpenAt(0)} />}
            {angles.map((p, i) => (
              <Thumb key={p} path={p} label={`${name || 'This character'}, ${(ANGLE_LABEL[i] || `angle ${i + 2}`).toLowerCase()}`} onOpen={() => setOpenAt(i + 1)} />
            ))}
          </>
        )}
      </div>

      {/*
        SAY WHAT THE SHEET IS WORTH. A single portrait locks a face from the front and re-invents the head at
        any other angle, so whether the turnaround exists is the difference between a character who holds
        across a camera move and one who does not. It was silent either way.
      */}
      {facePath && (
        <p className={`mt-1 ${TYPE.micro} text-secondary-400 dark:text-secondary-500`} data-testid="sheet-note">
          {angles.length > 0
            ? `Locked from ${angles.length + 1} angles — every shot keys their identity from these.`
            : loading
              ? 'The extra angles are being drawn.'
              : 'Front view only. A single angle holds a face from the front and re-invents it on a turn.'}
        </p>
      )}

      {images.length > 0 && (
        <ImageLightbox
          open={openAt !== null}
          onOpenChange={(o) => setOpenAt(o ? (openAt ?? 0) : null)}
          images={images}
          startIndex={openAt ?? 0}
          title={`${name || 'Character'} reference sheet`}
        />
      )}
    </div>
  );
};

export default CharacterSheet;
